import { Alert, AlertDescription } from '@crowdfpl/ui-web/alert';
import { Button } from '@crowdfpl/ui-web/button';
import { AlertTriangle, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ComboboxMobile } from '@/components/duels/ComboboxMobile';
import { cn } from '@/helpers/utils';
import { useAuth } from '@/hooks/auth';
import { useCreateDuel } from '@/hooks/data/duels';
import { notify } from '@/services/notifications';
import { useAuthModal } from '@/stores/ui';

interface Props {
  onFiltersChange: (query: string, playerA: { id: string; fpl_id: number; web_name: string } | null, playerB: { id: string; fpl_id: number; web_name: string } | null) => void;
  selectedPlayerA?: { id: string; fpl_id: number; web_name: string } | null;
  selectedPlayerB?: { id: string; fpl_id: number; web_name: string } | null;
  gameweekId?: string;
  isNextWeek: boolean;
  existingDuelExists: boolean;
  onCreated?: () => void;
}

export function DuelsSearchBarMobile({ onFiltersChange, selectedPlayerA, selectedPlayerB, gameweekId, isNextWeek, existingDuelExists, onCreated }: Props) {
  // local query state no longer needed (handled inside ComboboxMobile)
  const [selA, setSelA] = useState<{ id: string; fpl_id: number; web_name: string } | null>(null);
  const [selB, setSelB] = useState<{ id: string; fpl_id: number; web_name: string } | null>(null);
  const [openPlayerA, setOpenPlayerA] = useState(false);
  const [openPlayerB, setOpenPlayerB] = useState(false);
  const [createDuelMode, setCreateDuelMode] = useState(false);
  const create = useCreateDuel();
  const { isAuthenticated } = useAuth();
  const { showAuthModal } = useAuthModal();

  // Sync local selection with props (e.g., when cleared via badges outside)
  useEffect(() => {
    setSelA(selectedPlayerA ?? null);
    // Reset createDuelMode when players are cleared from outside
    if (!selectedPlayerA) {
      setCreateDuelMode(false);
    }
  }, [selectedPlayerA]);

  useEffect(() => {
    setSelB(selectedPlayerB ?? null);
    // Reset createDuelMode when players are cleared from outside
    if (!selectedPlayerB) {
      setCreateDuelMode(false);
    }
  }, [selectedPlayerB]);
  // debounced queries no longer needed; handled inside ComboboxMobile
  // searches now handled inside ComboboxMobile's sheet

  // Close other sheet when one opens
  const handlePlayerAOpenChange = (open: boolean) => {
    setOpenPlayerA(open);
    if (open) {
      setOpenPlayerB(false);
    }
  };

  const handlePlayerBOpenChange = (open: boolean) => {
    setOpenPlayerB(open);
    if (open) {
      setOpenPlayerA(false);
    }
  };

  // Handle player A selection
  const handlePlayerAChange = (p: { id: string; fpl_id: number; web_name: string } | null) => {
    setSelA(p);
    onFiltersChange('', p, selB);

    // If player is selected in createDuelMode and Player B is not selected, auto-open Player B
    if (p && createDuelMode && !selB) {
      setOpenPlayerB(true);
    } else if (p && createDuelMode) {
      // If both players are now selected, reset createDuelMode
      setCreateDuelMode(false);
    }

    // Reset createDuelMode if player is cleared
    if (!p) {
      setCreateDuelMode(false);
    }
  };

  // Handle player B selection
  const handlePlayerBChange = (p: { id: string; fpl_id: number; web_name: string } | null) => {
    setSelB(p);
    onFiltersChange('', selA, p);

    // Reset createDuelMode after Player B is selected
    if (p && createDuelMode) {
      setCreateDuelMode(false);
    }

    // Reset createDuelMode if player is cleared
    if (!p) {
      setCreateDuelMode(false);
    }
  };

  // Handle Create Duel button click
  const handleCreateDuelClick = () => {
    if (!selA && !selB) {
      setCreateDuelMode(true);
      setOpenPlayerA(true);
      return;
    }
    if (selA && !selB) {
      setCreateDuelMode(true);
      setOpenPlayerB(true);
      return;
    }
    if (!selA && selB) {
      setCreateDuelMode(true);
      setOpenPlayerA(true);
      return;
    }
    // Both players selected - create duel
    if (!isAuthenticated) {
      showAuthModal();
      return;
    }
    if (selA && selB && !existingDuelExists) {
      create.mutate(
        {
          player_a_id: selA.id,
          player_b_id: selB.id,
          gameweek_id: gameweekId,
        },
        {
          onSuccess: () => {
            notify.success('Duel created');
            setCreateDuelMode(false);
            onCreated?.();
          },
        },
      );
    }
  };

  // Get button label
  const getCreateButtonLabel = () => {
    if (create.isPending) return 'Creating...';
    if (!selA && !selB) return 'Create Duel';
    if (selA && !selB) return `${selA.web_name} vs ...`;
    if (!selA && selB) return `... vs ${selB.web_name}`;
    return `Create ${selA?.web_name} vs ${selB?.web_name}`;
  };

  // Get custom title for sheets when one player is selected
  const getPlayerATitle = () => {
    if (selB) return `Select Player A vs ${selB.web_name}`;
    return undefined;
  };

  const getPlayerBTitle = () => {
    if (selA) return `Select Player B vs ${selA.web_name}`;
    return undefined;
  };

  const hasBothPlayers = !!(selA && selB);
  const isSelfDuel = !!(selA && selB && selA.id === selB.id);
  const isButtonDisabled = create.isPending || (hasBothPlayers && (existingDuelExists || isSelfDuel));

  return (
    <div className="bg-background py-2 border-b border-border/20">
      <div className={cn('flex items-center gap-1', 'max-[340px]:flex-col max-[340px]:gap-2')}>
        <ComboboxMobile
          id="playerA"
          placeholder="Player A"
          value={selA}
          onChange={handlePlayerAChange}
          className="flex-1"
          open={openPlayerA}
          onOpenChange={handlePlayerAOpenChange}
          customTitle={getPlayerATitle()}
        />
        <div className={cn('flex items-center justify-center', 'max-[340px]:order-first')}>
          <div className="w-px h-6 bg-border" />
          <span className="text-xs text-muted-foreground px-2">vs</span>
          <div className="w-px h-6 bg-border" />
        </div>
        <ComboboxMobile
          id="playerB"
          placeholder="Player B"
          value={selB}
          onChange={handlePlayerBChange}
          excludePlayer={selA}
          className="flex-1"
          open={openPlayerB}
          onOpenChange={handlePlayerBOpenChange}
          customTitle={getPlayerBTitle()}
        />
      </div>

      {/* Create Duel Button - only show for next gameweek */}
      {isNextWeek && (
        <div className="mt-2">
          <Button
            onClick={handleCreateDuelClick}
            disabled={isButtonDisabled}
            variant={isButtonDisabled && hasBothPlayers ? 'outline' : 'default'}
            className="w-full h-9 text-sm"
          >
            {!isButtonDisabled && <Plus className="h-4 w-4 mr-2" />}
            <span className="truncate">{getCreateButtonLabel()}</span>
          </Button>
        </div>
      )}

      {/* Self-duel warning */}
      {selA && selB && selA.id === selB.id && (
        <Alert className="mt-2 py-2">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-sm">
            You can't create a duel of {selA.web_name} against himself.
          </AlertDescription>
        </Alert>
      )}

    </div>
  );
}

